import React, { useEffect, useState } from 'react';
import i18next from 'i18next';
import { push } from 'redux-first-history';
import {
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Popover,
    Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import { makeStyles } from '@mui/styles';
import {
    EditLocationAltOutlined,
    LocalShippingOutlined,
    SettingsSuggestOutlined
} from '@mui/icons-material';
import { customername } from '@/index';
import { useNavigate } from 'react-router-dom';
import { GENERAL_SETTING, VEHICLE_EDITOR, TERRITORY } from '@/config/paths';

interface EditorProps {
    anchorEl: HTMLElement | null;
    toggle: () => void;
}

interface RootState {
    core: {
        ui: string[];
    };
}

const useStyles = makeStyles(theme => ({
   listItem: {
        cursor: 'pointer',
        paddingTop: theme.spacing(1.2),
        paddingBottom: theme.spacing(1.2),
        transition: 'all 0.2s ease-in-out',

        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            '& $iconWrapper': {
                backgroundColor: theme.palette.primary.main,
            },
            
            '& $customIcon': {
                color: theme.palette.common.white,
            },
        },
    },

    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '35px',
        height: '35px',
        minWidth: '0px',
        borderRadius: '50%',
        backgroundColor: theme.palette.grey[200],
        marginRight: theme.spacing(1),
        transition: 'all 0.3s ease-in-out',
    },
    customIcon: {
        fontSize: '20px',
        transition: 'color 0.3s ease-in-out',
    },
    '& $iconWrapper:hover $customIcon': {
        color: theme.palette.common.white,
    },
    listText: {
        fontSize: '0.95rem',
    },
}));

const SettingEditor: React.FC<EditorProps> = ({ anchorEl, toggle }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const navigate = useNavigate();
    const { ui } = useSelector((state: RootState) => state.core, isEqual);

    const changePath = (path: string) => dispatch(push(path));
    const isActive = (feature: string): boolean => !ui.includes(feature);

    const Goto = (path: string) => {
       toggle(); 
        const isAddress = path === 'address';
        changePath(`/${customername}${isAddress ? '' : 'settings/'}${path}`);
    };

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            style={{ zIndex: 999999 }}
            onClose={toggle}
        >
            <List style={{ padding: 0, minWidth: '220px' }}>
                {isActive('no_settings') && (
                    <ListItem
                        divider
                        id="userlane-topheader-settings-settings"
                        onClick={() => Goto(GENERAL_SETTING)}
                        className={classes.listItem}
                    >
                        <ListItemIcon className={classes.iconWrapper}>
                            <SettingsSuggestOutlined className={classes.customIcon} />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography className={classes.listText} data-cy="general">
                                    {i18next.t('common:settings.allSetting')}
                                </Typography>
                            }
                        />
                    </ListItem>
                )}

                {isActive('no_vehicles') && (
                    <ListItem
                        divider
                        className={classes.listItem}
                        onClick={() => Goto(VEHICLE_EDITOR)}
                    >
                        <ListItemIcon className={classes.iconWrapper}>
                            <LocalShippingOutlined className={classes.customIcon} />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography className={classes.listText} data-cy="vehicles">
                                    {i18next.t('common:vehicleEditor.vehicleEdit')}
                                </Typography>
                            }
                        />
                    </ListItem>
                )}
                {isActive('no_territory') && (
                    <ListItem
                        divider
                        className={classes.listItem}
                        onClick={() => Goto(TERRITORY)}
                    >
                        <ListItemIcon className={classes.iconWrapper}>
                            <EditLocationAltOutlined className={classes.customIcon} />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography className={classes.listText} data-cy="territory">
                                    Gebietseditor
                                </Typography>
                            }
                        />
                    </ListItem>
                )}
            </List>
        </Popover>
    );
};

export default SettingEditor;
